import {Component, SplitPane} from 'substance'

import {DWTopbar} from './components/topbar/components/TopbarComponent'
import {DWSidebar} from './components/sidebar/SidebarComponent'
import {DialogPlaceholderComponent} from './components/dialog/DialogPlaceholderComponent'
import {IdfComponent} from './components/idf/IdfComponent'
import {Event} from '../lib/constants/Event'

import './scss/dw.scss'

/**
 * Main Digital Writer Component
 *
 * @extends Component
 */
class DWAppComponent extends Component {
    constructor(...args) {
        super(...args)

        this.configurator = this.context.configurator
        this.componentRegistry = this.context.componentRegistry
        this.promptUserBeforeUnload = true

        this.context.api.events.on('__internal', Event.DOCUMENT_SAVE_FAILED, this.showSaveFailedDialog.bind(this))
        this.context.api.events.on('__internal', Event.DOCUMENT_SAVED, this._updateTitle.bind(this))
        this.context.api.events.on('__internal', Event.DISABLE_UNLOAD_WARNING, () => {
            this.promptUserBeforeUnload = false
        })
        this.context.api.events.on('__internal', Event.ENABLE_UNLOAD_WARNING, () => {
            this.promptUserBeforeUnload = true
        })

        window.addEventListener('beforeunload', this.unloadListener.bind(this), false)
        window.addEventListener('resize', this.resizeListener.bind(this), false)

        this._updateTitle()
    }

    getInitialState() {
        return {
            readmode: this.context.api.settings.get(
                'se.infomaker.readmode.settings',
                'active',
                false
            )
        }
    }

    didMount() {
        setTimeout(() => {
            if (this.context.api.hasLocalConfiguration()) {
                this.context.api.ui.showNotification(
                    '__app',
                    'Warning',
                    'You are loading the Writer with a local configuration. Only do this if you know what you are doing.'
                )
            }
        }, 100)
        this.context.api.events.on('dwappcomponent', Event.SETTINGS_CHANGED, (ev) => {
            if (ev.data.key === 'se.infomaker.readmode.settings.active') {
                this.extendState({readmode: ev.data.value})
            }
        })
    }

    dispose() {
        this.context.api.events.off('__internal', Event.DOCUMENT_SAVE_FAILED)
        this.context.api.events.off('__internal', Event.DOCUMENT_SAVED)
        this.context.api.events.off('__internal', Event.DISABLE_UNLOAD_WARNING)
        this.context.api.events.off('__internal', Event.ENABLE_UNLOAD_WARNING)
        this.context.api.events.off('dwappcomponent', Event.SETTINGS_CHANGED)

        window.removeEventListener('beforeunload', this.unloadListener.bind(this), false)
        window.removeEventListener('resize', this.resizeListener.bind(this), false)
    }

    render($$) {
        const NotificationListComponent = this.componentRegistry.get('notification-list')
        const {api} = this.context
        let classNames = 'dwc-writer'

        const showExperimental = api.settings.get(
            'se.infomaker.devtools',
            'experimental',
            false
        )

        if (showExperimental) {
            classNames += ' dwc-experimental'
        }

        if (this.state.readmode) {
            classNames += ' dwc-readmode'
        }

        const disableSpellcheck = this.configurator.getWriterConfigValue('disableSpellcheck', false)

        return $$('div')
            .addClass(classNames)
            .append([
                $$(DialogPlaceholderComponent).addClass('modal-placeholder'),
                $$(DWTopbar, {
                    topBarComponents: this.configurator.getTopbarComponents()
                }).ref('dw-topbar'),
                $$(SplitPane, {
                    splitType: 'vertical'
                }).ref('dw-splitpanes')
                    .append([
                        $$(IdfComponent, {disableSpellcheck}).ref('dw-idf'),
                        $$(DWSidebar).ref('dw-sidebar')
                    ]),
                $$(NotificationListComponent).ref('notification-area')
            ]).ref('dw-app')
    }

    /**
     * Needed to make sure popovers stays positioned correctly
     * Maybe it should be moved to topbar...
     */
    resizeListener(ev) {
        if (!this.resizeTimeout) {
            this.resizeTimeout = setTimeout(() => {
                this.resizeTimeout = null
                this.context.api.events.trigger(
                    'DWAppComponent',
                    Event.BROWSER_RESIZE, ev
                )
            }, 66) // About 15 fps
        }
    }

    unloadListener(ev) {
        if (this.promptUserBeforeUnload && this.context.state.document.changed) {
            const message = 'Document is not saved'
            ev.returnValue = message
            return message
        }
    }

    showSaveFailedDialog(e) {
        try {
            const reason = (e.data && e.data.reason) ? e.data.reason : undefined
            const resolverClass = this.context.api.configurator.getConflictHandler(reason)

            if (resolverClass) {

                const uuid = (e.data && e.data.uuid) ? e.data.uuid : undefined

                this.context.api.ui.showDialog(
                    resolverClass,
                    {
                        uuid: uuid,
                        error: e,
                        close: this.hideDialog.bind(this)
                    },
                    {
                        title: this.props.api.getLabel('A problem occurred'),
                        primary: false,
                        global: true
                    }
                )

                return
            }

        }
        catch(ex) {
            // Got error when resolving resolver, continuing
            console.error('Error resolving conflict handler', ex)
        }

        // Default behavior
        this.context.api.ui.showMessageDialog(this._getErrorMessagesForDialog(e))
    }

    _getErrorMessagesForDialog(e) {
        if (e.data && e.data.errors) {
            return e.data.errors.map((error) => {
                return {
                    type: 'error',
                    message: error.error
                }
            })
        } else if (e.data && e.data.message) {
            return [
                {type: 'error', message: e.data.message}
            ]
        } else if (e.message) {
            return [
                {type: 'error', message: e.message}
            ]
        } else {
            return [{type: 'error', message: 'Unknown error'}]
        }
    }

    /**
     * Uses the document nodes to set the browser tab/window title
     * Uses an array to specify which nodeType that should be used as title
     * Uses only the first nodeType in the array and omits the others
     */
    _updateTitle() {
        const documentNodes = this.context.api.document.nodes()

        const nodeToUse = documentNodes.find((node) => {
            return ['headline', 'preamble', 'paragraph'].includes(node.type) && node.content.length > 0
        })

        let title = 'Digital Writer'
        if (nodeToUse) {
            title = nodeToUse.content.substr(0, 100)
        }

        this.context.api.browser.setTitle(title)
    }
}

export {DWAppComponent}
